import { useNegativeStocks } from '@Balance/Hooks/useNegativeStocks/useNegativeStocks'
import { NegativeClientStock } from '@Balance/Hooks/useNegativeStocks/useNegativeStocks.types'
import { useProducts } from '@Products/Hooks/useProducts'
import { RoutesPaths } from '@Balance/RoutesPaths'
import { Container } from '@Core/Components'
import { Input, InputSize } from '@Core/Components/Input/Input'
import { InputAutoComplete } from '@Core/Components/InputAutoComplete/InputAutoComplete'
import { MenuOptions } from '@Core/Components/MenuOptions/MenuOptions'
import { IMenuOption } from '@Core/Components/MenuOptions/MenuOptions.types'
import { formatRouteId } from '@Utils/Formatters/formatRouteId'
import { exportXlsx } from '@Utils/Support/exportXlsx'
import { API } from '@Services/api'
import {
    Chip,
    FormControlLabel,
    Grid,
    Switch,
    TablePagination,
} from '@material-ui/core'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { TfiExport } from 'react-icons/tfi'
import { usePDF } from '@react-pdf/renderer'
import { NegativeStockDocument } from '@Balance/Reports/Pdf/Pdf'
import {
    exportNegativeStockByClientData,
    exportNegativeStockDictionary,
    exportNegativeStockValueDictionary,
} from '@Balance/Common/BalanceCard/BalanceCard.utils'
import {
    ActionsRow,
    CardTop,
    CardsGrid,
    ClientCard,
    ClientName,
    Divider,
    EmptyState,
    Header,
    NegativeBadge,
    ProductAmount,
    ProductFilterWrapper,
    ProductList,
    ProductName,
    ProductRow,
    SwitchWrapper,
    TitleContainer,
} from './NegativeBalance.styles'

export const NegativeBalance = () => {
    const { negativeStocks } = useNegativeStocks()
    const { products, getProducts } = useProducts(API)
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(10)
    const [column, setColumn] = useState(false)
    const [searchText, setSearchText] = useState('')
    const [selectedProduct, setSelectedProduct] = useState<string | null>(null)
    const navigate = useNavigate()

    useEffect(() => {
        getProducts()
    }, [])

    const formatArray = ({ client, product, _id }: NegativeClientStock) => {
        return {
            _id: _id[0],
            client: client[0],
            product: product.map(({ name: [name], _id: [_id], amount }) => ({
                name,
                _id,
                amount,
            })),
        }
    }

    const getFilteredStocks = (): NegativeClientStock[] => {
        return negativeStocks.filter((entity) => {
            const { client, product } = formatArray(entity)

            const matchesSearch =
                client?.corporateName
                    .toLowerCase()
                    .includes(searchText.toLowerCase()) ||
                product?.some(({ name }) =>
                    String(name)
                        ?.toLowerCase()
                        .includes(searchText.toLowerCase())
                )

            const matchesProduct = selectedProduct
                ? product?.some(
                      ({ name }) =>
                          String(name)?.toLowerCase() ===
                          selectedProduct.toLowerCase()
                  )
                : true

            return matchesSearch && matchesProduct
        })
    }

    const filtered = getFilteredStocks()

    const getProductsForCard = (negativeStock: NegativeClientStock) => {
        if (!selectedProduct) return negativeStock.product
        return negativeStock.product.filter((p) => {
            const name = Array.isArray(p.name) ? p.name[0] : p.name
            return String(name)?.toLowerCase() === selectedProduct.toLowerCase()
        })
    }

    const stockDocument = NegativeStockDocument({
        title: selectedProduct
            ? `Clientes com Saldo Negativo - ${selectedProduct}`
            : 'Clientes com Saldo Negativo',
        // @ts-ignore
        stocks: exportNegativeStockByClientData(filtered),
        withClient: true,
    })

    const [instance, update] = usePDF({
        document: stockDocument,
    })

    const exportOptions: IMenuOption[] = [
        {
            label: 'Exportar para Excel',
            onClick: () => {
                exportXlsx(
                    exportNegativeStockByClientData(filtered),
                    selectedProduct
                        ? `Clientes com Saldo Negativo - ${selectedProduct}`
                        : 'Clientes com Saldo Negativo',
                    {
                        columnsDict: exportNegativeStockDictionary,
                        valuesDict: exportNegativeStockValueDictionary,
                        sheetName: 'Saldo Negativo',
                    }
                )
            },
        },
        {
            label: 'Exportar para PDF',
            onClick: () => {},
            href: instance.url || undefined,
            attrs: {
                download: selectedProduct
                    ? `Clientes com Saldo Negativo - ${selectedProduct}.pdf`
                    : 'Clientes com Saldo Negativo.pdf',
                target: '_blank',
            },
        },
    ]

    useEffect(() => {
        update(stockDocument)
    }, [negativeStocks, selectedProduct])

    const getPaginate = (page: number, limit: number) => {
        const start = (page - 1) * limit
        return [...filtered].splice(start, limit)
    }

    const goTo = (id: string) => {
        navigate(formatRouteId(RoutesPaths.ClientDetail, id))
    }

    const productOptions = products
        .map((p) => ({
            label: p.name,
            value: p._id,
            ...p,
        }))
        .sort((a, b) => (a.label || '').localeCompare(b.label || ''))

    const handleProductSelect = (_: any, value: any) => {
        setSelectedProduct(value?.label ?? null)
        setPage(1)
    }

    const handleClearProduct = () => {
        setSelectedProduct(null)
        setPage(1)
    }

    const paginated = getPaginate(page, limit)

    return (
        <Container>
            <Header>
                <TitleContainer>
                    <h2>Clientes com saldo negativo</h2>
                    <MenuOptions
                        data-testid="negative-stock-export"
                        menuList={exportOptions}
                    >
                        <TfiExport />
                    </MenuOptions>
                </TitleContainer>

                <ActionsRow className="flex">
                    <Input
                        className="flex-1"
                        size={InputSize.Small}
                        useUnform={false}
                        value={searchText}
                        placeholder="Buscar cliente"
                        onChange={(e: any) => setSearchText(e.target.value)}
                        name={'search'}
                        fullWidth
                    />

                    <ProductFilterWrapper className="flex-1">
                        <InputAutoComplete
                            className="flex-1"
                            label="Filtrar por produto"
                            onChange={handleProductSelect}
                            size="small"
                            inputProps={{
                                name: 'product-filter',
                                useUnform: false,
                            }}
                            results={productOptions}
                            name="product-filter"
                        />
                        {selectedProduct && (
                            <Chip
                                label={selectedProduct}
                                onDelete={handleClearProduct}
                                color="primary"
                                size="small"
                            />
                        )}
                    </ProductFilterWrapper>

                    <SwitchWrapper>
                        <FormControlLabel
                            control={
                                <Switch
                                    onChange={() => setColumn(!column)}
                                    checked={column}
                                />
                            }
                            label="Colunas"
                        />
                    </SwitchWrapper>
                </ActionsRow>
            </Header>

            <CardsGrid $columns={column}>
                {paginated.length === 0 && (
                    <EmptyState>Nenhum cliente encontrado.</EmptyState>
                )}
                {paginated.map((negativeStock) => {
                    const visibleProducts = getProductsForCard(negativeStock)
                    if (visibleProducts.length === 0) return null
                    return (
                        <ClientCard
                            key={negativeStock._id[0]}
                            onClick={() => goTo(negativeStock._id[0])}
                        >
                            <CardTop>
                                <ClientName>
                                    {negativeStock.client?.[0]?.corporateName ??
                                        'N/A'}
                                </ClientName>
                                <NegativeBadge>
                                    {visibleProducts.length}{' '}
                                    {visibleProducts.length === 1
                                        ? 'produto'
                                        : 'produtos'}
                                </NegativeBadge>
                            </CardTop>
                            <Divider />
                            <ProductList>
                                {visibleProducts.map((product) => (
                                    <ProductRow key={product._id}>
                                        <ProductName>
                                            {product.name ?? 'N/A'}
                                        </ProductName>
                                        <ProductAmount>
                                            {product.amount ?? 0}
                                        </ProductAmount>
                                    </ProductRow>
                                ))}
                            </ProductList>
                        </ClientCard>
                    )
                })}
            </CardsGrid>

            <Grid container>
                <Grid item xs={12}>
                    <TablePagination
                        labelRowsPerPage="Itens por página"
                        rowsPerPageOptions={[10, 25, 100, 10000, 100000]}
                        component="div"
                        count={filtered.length}
                        rowsPerPage={limit}
                        labelDisplayedRows={({ from, to, count }) =>
                            from ? `${from}-${to} de ${count}` : `1 de ${count}`
                        }
                        page={Number(page - 1)}
                        onPageChange={(_, page) => setPage(page + 1)}
                        onRowsPerPageChange={(e) => {
                            setLimit(Number(e.target.value))
                            setPage(1)
                        }}
                    />
                </Grid>
            </Grid>
        </Container>
    )
}
